import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { useLayerDriftFindings } from "@/v2-backoffice-client/layer/drift/diagnostics/useLayerDriftFindings"
import type { LayerEditorField } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"

export type LayerDocumentDriftDotProps = ComponentProps<"span"> & {
  featureCode?: string
  field: LayerEditorField
}

/**
 * A dot on a document tab whose JSON still names something the upstream
 * service changed.
 *
 * Without it, drift is only legible from the Drift tab: an operator who
 * fixes the Configuration reference has no way of knowing the Table
 * definition names the same dead field, short of going back and reading
 * the list again. The dot puts that where they are already looking, and
 * turns "what else is affected?" into a glance across the tab strip.
 *
 * Reflects the saved documents, since that is what the backend scanned —
 * it clears on the refetch that follows a save, while the squiggles
 * inside the editor clear as soon as the text changes.
 */
function LayerDocumentDriftDot(props: LayerDocumentDriftDotProps) {
  const { featureCode, field, className, ...spanProps } = props

  const findings = useLayerDriftFindings(featureCode, field)
  if (findings.length === 0) return null

  const isBreaking = findings.some((finding) => finding.severity === "error")

  // A native title rather than the Tooltip primitive: the dot sits inside
  // a tab button, and nesting a second interactive trigger in there buys
  // a hover card at the cost of two overlapping click targets.
  return (
    <span
      role="img"
      aria-label={describe(findings.length)}
      title={describe(findings.length)}
      className={cn(
        "ml-1.5 size-1.5 shrink-0 rounded-full",
        isBreaking ? "bg-destructive" : "bg-amber-500",
        className,
      )}
      {...spanProps}
    />
  )
}

function describe(count: number): string {
  return count === 1
    ? "1 upstream change is referenced by this document"
    : `${count} upstream changes are referenced by this document`
}

export default LayerDocumentDriftDot
